import { HELP_CATEGORIES, type HelpArticle, type HelpCategory, type HelpSource } from './types';
import type { Permission } from '../../context/RoleContext';

/**
 * Markdown-backed help source.
 *
 * Articles live in src/content/help/*.md and are bundled with the app, which
 * means they work offline through the service worker and version alongside the
 * code that they describe. Editing copy needs a deploy — when that becomes the
 * bottleneck, add a DB-backed HelpSource and switch the export in ./index.ts.
 */

const modules = import.meta.glob('../../content/help/*.md', {
    query: '?raw',
    import: 'default',
    eager: true,
}) as Record<string, string>;

/**
 * Minimal frontmatter parser — `key: value` lines between `---` fences. We do
 * not pull in gray-matter for this; the format is ours and stays this simple.
 */
function parseFrontmatter(raw: string): { meta: Record<string, string>; body: string } {
    const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
    if (!match) return { meta: {}, body: raw };

    const meta: Record<string, string> = {};
    for (const line of match[1].split(/\r?\n/)) {
        const idx = line.indexOf(':');
        if (idx === -1) continue;
        const key = line.slice(0, idx).trim();
        const value = line.slice(idx + 1).trim().replace(/^["']|["']$/g, '');
        if (key) meta[key] = value;
    }
    return { meta, body: match[2] };
}

function isCategory(value: string): value is HelpCategory {
    return (HELP_CATEGORIES as readonly string[]).includes(value);
}

function toArticle(path: string, raw: string): HelpArticle {
    const { meta, body } = parseFrontmatter(raw);
    const slug = path.split('/').pop()!.replace(/\.md$/, '');
    const category = meta.category && isCategory(meta.category) ? meta.category : 'Start here';

    return {
        slug,
        title: meta.title || slug,
        category,
        summary: meta.summary || '',
        icon: meta.icon || 'FileText',
        order: Number(meta.order ?? 99),
        permission: (meta.permission as Permission) || undefined,
        visual: meta.visual || undefined,
        body: body.trim(),
    };
}

const articles: HelpArticle[] = Object.entries(modules)
    .map(([path, raw]) => toArticle(path, raw))
    .sort((a, b) => {
        const catDelta = HELP_CATEGORIES.indexOf(a.category) - HELP_CATEGORIES.indexOf(b.category);
        return catDelta !== 0 ? catDelta : a.order - b.order;
    });

export const markdownHelpSource: HelpSource = {
    async listArticles() {
        return articles;
    },
    async getArticle(slug: string) {
        return articles.find((a) => a.slug === slug) ?? null;
    },
};
